import { Helmet } from "react-helmet";
import React from "react";
import styled from "styled-components";
import { Box, Button, Grid, Paper, Stack, Tooltip } from "@mui/material";
import { useAuth } from "src/components/providers/auth-context";
import LinkedInButton from "src/scenes/personal-profile/linked-in-button";
import TwitterButton from "src/scenes/personal-profile/twitter-button";
import EmbeddedButton from "src/scenes/personal-profile/embedded-button";
import CustomTooltip from "src/components/custom-tooltip";
import Portal from "src/components/portal";
import DemoBarPersonal from "src/components/demo-bar-personal";
import DemoBarDemo from "src/components/demo-bar-demo";
import {
    ActiveIntegrationsProvider,
    useActiveIntegrations,
} from "src/scenes/dashboard/active-integrations-provider";
import VisibilityIcon from "@mui/icons-material/Visibility";
import request from "src/utils/request";
import EmbeddedModal from "src/scenes/personal-profile/embedded-modal";
import { Skeleton } from "@mui/lab";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BadgeAchieventProfile from "src/components/badge-achievent-profile";
import LinkButton from "src/scenes/personal-profile/link-button";
import LinkModal from "src/scenes/personal-profile/link-modal";
import { ToastContext } from "src/components/providers/toast-provider";
import { bigNumberFormatter } from "src/utils/number-formatter";

const CardContainer = styled(Paper)`
    border-radius: 14px;
    border: 2px solid transparent;
`;

const MainText = styled.p`
    font-size: 13px;
    padding: 12px;
    margin: 0;
`;

const SecondaryText = styled.p`
    font-size: 11px;
    margin: 0;
`;

const ValueText = styled.p`
    font-size: 11px;
    border-radius: 30px;
    background-color: #333333;
    height: 24px;
    width: 44px;
    margin: 0 0 0 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
`;

const AchievementsStack = styled(Stack)`
    background-color: #333333;
    border-radius: 14px;
    margin-top: 8px;
    padding-top: 5px;
    padding-left: 5px;
    min-height: 86px;
`;

const ProfileButton = styled(Button)`
    text-transform: none;
    font-size: 12px;
`;

const UserImage = styled.img`
    border-radius: 70px;
    border: 3px solid #f9cd45;
    min-height: 80px;
    min-width: 80px;
`;

const RankingBox = styled(Box)`
    width: 48px;
    height: 21px;
    background-color: #f9cd45;
    color: #333333;
    border-radius: 4px;
`;

const RankNumber = styled.p`
    margin: 0;
    font-weight: 800;
    padding-left: 4px;
`;

const PersonalProfileCard = () => {
    const [embeddedModal, setEmbeddedModal] = React.useState(false);
    const [linkModal, setLinkModal] = React.useState(false);
    const { user, isDemo, updateUser } = useAuth();
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [profileData, setProfileData] = React.useState([]);
    const [userData, setUserData] = React.useState();
    const { activeIntegrations } = useActiveIntegrations();

    const checkAllIntegrationsDisconnected = () => {
        return (
            activeIntegrations &&
            activeIntegrations.data &&
            activeIntegrations.data.integrations.every(
                (integration) => integration.connected === false
            )
        );
    };
    const isUnintegrated = checkAllIntegrationsDisconnected();
    const isProfilePrivate = user && user.profileVisibility === "private";
    const getPersonalProfile = async () => {
        if (activeIntegrations.status !== "SUCCESS") return;
        const path = window.location.pathname;
        const hasHandle = path.match(/\/personal-profile\/(.+)/);
        const handle = hasHandle ? hasHandle[1] : null;
        let response;
        if (isDemo || isUnintegrated) {
            response = (await import("src/scenes/personal-profile/personal-profile-mock-data"))
                .default;
        } else if (handle) {
            response = await request(`/api/users/${handle}/stats`, { method: "GET" });
        } else {
            response = await request(`/api/users/me/stats`, { method: "GET" });
        }
        setProfileData(response);
    };

    const getUser = async () => {
        const path = window.location.pathname;
        const hasHandle = path.match(/\/personal-profile\/(.+)/);
        const handle = hasHandle ? hasHandle[1] : null;
        let response;
        if (handle) {
            response = await request(`/api/users/public/${handle}`, { method: "GET" });
        } else {
            response = user;
        }
        setUserData(response);
    };

    React.useEffect(() => {
        getPersonalProfile();
        getUser();
    }, [activeIntegrations]);

    const handleEnableProfile = async () => {
        try {
            await request("/api/users/me", {
                method: "POST",
                body: `{"profileVisibility":"public"}`,
            });
            updateUser();
            setMessage(`Your personal profile is now public!`);
            setSeverity("success");
            setIsToastTriggered(true);
        } catch (e) {
            setMessage(`Could make your personal profile public at this time.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };
    const filterObjectValue = (stats) => {
        if (!Array.isArray(stats)) {
            return stats;
        }

        const filteredStats = stats.filter((stat) => {
            return typeof stat.value !== "object";
        });

        return filteredStats;
    };
    const keepOnlyObjects = (stats) => {
        if (!Array.isArray(stats)) {
            return stats;
        }

        const filteredStats = stats.filter(
            (item) => typeof item.value === "object" && item.value !== null
        );
        return filteredStats;
    };
    const toSort = profileData.stats;
    const sortedProfileData = filterObjectValue(toSort);
    const sortedProfileObjects = keepOnlyObjects(profileData.stats);

    return (
        <ActiveIntegrationsProvider>
            <Helmet>
                <title>Adadot - Personal Profile</title>
            </Helmet>
            <Portal>
                <DemoBarPersonal />
                <DemoBarDemo />
            </Portal>
            <Stack direction="column" alignItems="flex-end">
                {profileData && profileData.length !== 0 ? (
                    <>
                        <CardContainer>
                            <Grid container justifyContent="center">
                                <Grid item id="user" md={2}>
                                    <Stack
                                        direction="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{ height: "338px" }}
                                    >
                                        <div
                                            direction="column"
                                            alignItems="center"
                                            style={{ height: "95px" }}
                                        >
                                            <UserImage
                                                src={userData && userData.picture}
                                                alt="Profile"
                                                style={{
                                                    borderRadius: "70px",
                                                    maxWidth: "80px",
                                                    maxHeight: "80px",
                                                    position: "relative",
                                                    top: 0,
                                                    left: 0,
                                                }}
                                            />
                                            {userData &&
                                                userData.rank &&
                                                userData.rankVisibility === "public" && (
                                                    <CustomTooltip title="Adadot Ranking">
                                                        <RankingBox
                                                            style={{
                                                                position: "relative",
                                                                top: "-21px",
                                                                left: "17px",
                                                            }}
                                                        >
                                                            <RankNumber>
                                                                # {userData.rank}
                                                            </RankNumber>
                                                        </RankingBox>
                                                    </CustomTooltip>
                                                )}
                                        </div>
                                        <MainText>{userData && userData.displayName}</MainText>
                                        <SecondaryText>
                                            {userData && userData.jobTitle}
                                        </SecondaryText>
                                        <SecondaryText>
                                            {userData && userData.website}
                                        </SecondaryText>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            pt={1}
                                        >
                                            <TwitterButton
                                                user={user}
                                                isProfilePrivate={isProfilePrivate}
                                            />
                                            <LinkedInButton
                                                user={user}
                                                isProfilePrivate={isProfilePrivate}
                                            />
                                            <EmbeddedButton
                                                handleClick={() => setEmbeddedModal(true)}
                                                isProfilePrivate={isProfilePrivate}
                                            />
                                            <LinkButton
                                                handleClick={() => setLinkModal(true)}
                                                isProfilePrivate={isProfilePrivate}
                                            />
                                        </Stack>
                                    </Stack>
                                </Grid>
                                <Grid item id="data" md={10} p={4}>
                                    <Stack direction="row">
                                        <Grid
                                            container
                                            spacing={2}
                                            md={12}
                                            justifyContent="flex-start"
                                        >
                                            {sortedProfileData.map((item) => {
                                                return (
                                                    <Grid item key={item.title} md={6} xs={12}>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                        >
                                                            <SecondaryText>
                                                                {item.title}
                                                            </SecondaryText>
                                                            {item.value > 1_000 ? (
                                                                <Tooltip title={item.value}>
                                                                    <ValueText>
                                                                        {bigNumberFormatter(
                                                                            item.value
                                                                        )}
                                                                    </ValueText>
                                                                </Tooltip>
                                                            ) : (
                                                                <ValueText>{item.value}</ValueText>
                                                            )}
                                                        </Stack>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                        <Grid
                                            item
                                            key={sortedProfileObjects[0].title}
                                            md={4}
                                            xs={12}
                                        >
                                            <Grid
                                                direction="column"
                                                alignItems="flex-start"
                                                justifyContent="space-between"
                                                style={{
                                                    width: "100%",
                                                    marginLeft: "16px",
                                                }}
                                            >
                                                <SecondaryText
                                                    style={{
                                                        opacity: "0.5",
                                                        paddingTop: "2px",
                                                        paddingBottom: "2px",
                                                    }}
                                                >
                                                    {sortedProfileObjects[0].title}
                                                </SecondaryText>
                                                <Grid
                                                    direction="column"
                                                    justifyContent="space-between"
                                                >
                                                    {Object.entries(
                                                        sortedProfileObjects[0].value
                                                    ).map(([title, value]) => (
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            mt={2}
                                                        >
                                                            <SecondaryText>{title}</SecondaryText>
                                                            {value > 1_000 ? (
                                                                <Tooltip title={value}>
                                                                    <ValueText
                                                                        style={{
                                                                            margin: 0,
                                                                        }}
                                                                    >
                                                                        {bigNumberFormatter(value)}
                                                                    </ValueText>
                                                                </Tooltip>
                                                            ) : (
                                                                <ValueText
                                                                    style={{
                                                                        margin: 0,
                                                                    }}
                                                                >
                                                                    {value}
                                                                </ValueText>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Stack>

                                    <SecondaryText style={{ paddingTop: "26px", opacity: "0.4" }}>
                                        Latest Achievements
                                    </SecondaryText>
                                    <AchievementsStack direction="row" spacing={2}>
                                        <Grid container direction="row">
                                            {profileData &&
                                                profileData.achievements &&
                                                profileData.achievements.latest.map((achievement) =>
                                                    BadgeAchieventProfile({ achievement })
                                                )}
                                        </Grid>
                                    </AchievementsStack>
                                </Grid>
                            </Grid>
                        </CardContainer>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid item>
                                {user.profileVisibility && user.profileVisibility === "private" && (
                                    <Stack direction="row" alignItems="center">
                                        <VisibilityOffIcon
                                            style={{ opacity: "0.5", paddingRight: "8px" }}
                                        />
                                        <SecondaryText style={{ opacity: "0.5", fontSize: "12px" }}>
                                            Your profile card is only visible to you at the moment.
                                        </SecondaryText>
                                        <ProfileButton
                                            size="small"
                                            variant="text"
                                            onClick={handleEnableProfile}
                                        >
                                            Click here to make public.
                                        </ProfileButton>
                                    </Stack>
                                )}
                            </Grid>
                            <Grid item>
                                <CustomTooltip title="Your profile views">
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        p={1}
                                        style={{ opacity: "0.5" }}
                                    >
                                        <VisibilityIcon fontSize="small" />
                                        <p>{profileData.views}</p>
                                    </Stack>
                                </CustomTooltip>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="60vw"
                        height="30vh"
                        sx={{ borderRadius: "14px" }}
                    />
                )}
            </Stack>
            <EmbeddedModal
                open={embeddedModal}
                handleClose={() => setEmbeddedModal(false)}
                user={user}
                isDemo
            />
            <LinkModal
                open={linkModal}
                handleClose={() => setLinkModal(false)}
                user={user}
                isDemo
            />
        </ActiveIntegrationsProvider>
    );
};

export default PersonalProfileCard;
